import useSetSearchTerm from 'hooks/useSetSearchTerm';
import { getFeatureFlagByNameSelector } from 'store/selectors';
import { setDisplayedSearchTerm, setProductSearchInput } from 'store/slices/sayt';
import { useAppDispatch, useAppSelector } from 'store/store';
import { StoreIdentifierName } from 'types';
import { handleSAYTEvent, handleTapAheadEvent } from 'utility/analytics-events';
import { getCookie, setCookie } from 'utility/cookie-utils';
import { checkStores, cleanSearchTerm, searchTypeAnalyticsReference } from 'utility/utility';
import {
  searchDataSelector,
  searchInputSelector,
  searchProductInputSelector,
} from 'store/selectors/saytSelector';
import { SearchProductResult } from './types';
import { envData } from '../../../../environments';

const getSearchUtils = (storeIdentifierName: StoreIdentifierName) => {
  const dispatch = useAppDispatch();
  const [setSearchTerm] = useSetSearchTerm();
  const saytCategoriesRouteChangeEnabled = useAppSelector(
    getFeatureFlagByNameSelector('saytCategoriesRouteChangeEnabled'),
  );
  const searchInput = useAppSelector(searchInputSelector);
  const productSearchInput = useAppSelector(searchProductInputSelector);
  const searchData = useAppSelector(searchDataSelector);

  const updateSearchTermSAYT = (term: string) => {
    // Analytics
    handleTapAheadEvent();
    setSearchTerm(term);
    dispatch(setDisplayedSearchTerm(term));
    setTimeout(() => {
      (document.querySelector('#searchInput') as HTMLInputElement)?.focus();
    }, 100);
  };

  const determineSuggestionHref = (term: string) => {
    // Get the store cookie
    const storeCookie = getCookie('setStoreCookie')
      ? decodeURIComponent(getCookie('setStoreCookie') as string)
      : '';
    // Parse the cookie
    const selectedStore = storeCookie.split('_')[2];
    const selectedStoreParam = selectedStore ? `&selectedStore=${selectedStore}` : '';
    const queryParams = `searchTerm=${term}&storeId=${envData[storeIdentifierName].storeId}&catalogId=${envData[storeIdentifierName].catalogId}&langId=-1&sType=SimpleSearch&resultCatEntryType=2&showResultsPage=true&fromPage=Search&searchSource=Q&pageView=&beginIndex=0&DSGsearchType=Keyword&pageSize=48${selectedStoreParam}`;
    return `${queryParams}`;
  };

  const submitSearch = (
    term: string,
    searchType: string,
    url?: string,
    isSeeAllResultsLink?: boolean,
  ) => {
    if (!term) return '';

    if (term.length > 2 && !isSeeAllResultsLink) {
      const terms = localStorage.getItem('recentTerms');
      let currentHistory = !terms ? [] : terms?.split(',');
      currentHistory = [term].concat(currentHistory);
      const historySet = new Set(currentHistory);
      currentHistory = Array.from(historySet);
      if (currentHistory.length > 10) {
        currentHistory = currentHistory.slice(0, 10);
      }
      localStorage.setItem('recentTerms', currentHistory.join(','));
    }

    setCookie(
      'searchRefinement',
      searchTypeAnalyticsReference[searchType]
        ? searchTypeAnalyticsReference[searchType]
        : searchType,
    );
    const cleanedTerm = cleanSearchTerm(term);
    setCookie('originSayT', (searchType !== 'enterSearchTerm').toString());

    if (searchType === 'category' && saytCategoriesRouteChangeEnabled) {
      return `${url}?enteredSearchTerm=${cleanedTerm}&SearchType=Category`;
    }
    return checkStores(`/search/SearchDisplay?${determineSuggestionHref(cleanedTerm)}`);
  };

  const getSearchTermUrl = (searchTerm: string) => {
    const cleanedTerm = cleanSearchTerm(searchTerm);
    return checkStores(`/search/SearchDisplay?${determineSuggestionHref(cleanedTerm)}`);
  };

  const evaluateViewAllUrl = () => {
    let url;
    if (productSearchInput?.url) {
      url = productSearchInput.url;
    } else if (productSearchInput?.term) {
      url = `/search/SearchDisplay?searchTerm=${productSearchInput.term}&storeId=${envData[storeIdentifierName].storeId}&catalogId=${envData[storeIdentifierName].catalogId}&langId=-1&sType=SimpleSearch&resultCatEntryType=2&showResultsPage=true&fromPage=Search&searchSource=Q&pageView=&beginIndex=0&DSGsearchType=Keyword&pageSize=48`;
    } else if (searchData?.suggestions?.keywords[0]) {
      url = `/search/SearchDisplay?searchTerm=${searchData.suggestions.keywords[0]}&storeId=${envData[storeIdentifierName].storeId}&catalogId=${envData[storeIdentifierName].catalogId}&langId=-1&sType=SimpleSearch&resultCatEntryType=2&showResultsPage=true&fromPage=Search&searchSource=Q&pageView=&beginIndex=0&DSGsearchType=Keyword&pageSize=48`;
    } else {
      url = `/search/SearchDisplay?searchTerm=${searchInput}&storeId=${envData[storeIdentifierName].storeId}&catalogId=${envData[storeIdentifierName].catalogId}&langId=-1&sType=SimpleSearch&resultCatEntryType=2&showResultsPage=true&fromPage=Search&searchSource=Q&pageView=&beginIndex=0&DSGsearchType=Keyword&pageSize=48`;
    }
    return url;
  };

  const clickSuggestedTerm = (searchType: string) => {
    setCookie(
      'searchRefinement',
      searchTypeAnalyticsReference[searchType]
        ? searchTypeAnalyticsReference[searchType]
        : searchType,
    );
    handleSAYTEvent(cleanSearchTerm(searchInput));
  };

  const getProductImage = (product: SearchProductResult, thumbnailApi: string) => {
    let color = '';
    if (product.color) {
      color = `_${product.color.replace(/[^\w]/gi, '_').replace(/(_+)\1+/g, '$1')}`;
    }
    const imageUrl = `${thumbnailApi + product.thumbnail + color}_is/?$DSG_ProductCard$`;
    return imageUrl;
  };

  const getSeoURL = (product: SearchProductResult) => {
    let seoURL = product.seoUrl ?? '';

    if (product.isPinned) {
      if (!seoURL.includes('Product=SuggestedPinnedProduct')) {
        seoURL += '?Product=SuggestedPinnedProduct';
      }
    } else if (!seoURL.includes('Product=ProductSuggest')) {
      seoURL += '?Product=ProductSuggest';
    }

    return seoURL;
  };

  const setProductSearchTerm = (payload: { term: string; type: string; url: string | null }) => {
    dispatch(setProductSearchInput(payload));
  };

  const searchFromSuggestion = async (searchString: string, type: string, url?: string) => {
    setCookie(
      'searchRefinement',
      searchTypeAnalyticsReference[type] ? searchTypeAnalyticsReference[type] : type,
    );
    setProductSearchTerm({
      term: searchString,
      type,
      url: url ?? null,
    });
  };

  return {
    updateSearchTermSAYT,
    submitSearch,
    getSearchTermUrl,
    evaluateViewAllUrl,
    clickSuggestedTerm,
    getProductImage,
    getSeoURL,
    searchFromSuggestion,
  };
};
export default getSearchUtils;
